import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useItemsApi } from "./useItemsApi";
import type { BiddingItemStatusUpdateRequest, BiddingItemStatusUpdateResult } from "../types";
import qs from "qs";
import toast from "react-hot-toast";
import { showApiErrorToast, type MutationConfig } from "./util/commonMutation";

/**
 * 出品情報の状態の各件数を取得
 */
export const useBiddingItemStatusCounts = (filters: BiddingItemStatusUpdateRequest, loginId: string) => {
  const { biddingItemStatusCounts } = useItemsApi();

  return useQuery<BiddingItemStatusUpdateResult, Error>({
    queryKey: ['biddingitemsstatusupdate', 'all', filters, loginId],

    queryFn: async() => {
      // フィルタ整形
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      return await biddingItemStatusCounts(queryString);
    }, 
    enabled: loginId !== "" && (!!loginId || (filters.sellerCode !== 0 && filters.farmerCode !== 0 && filters.biddingDate !== 0)),
  });
};

/**
 * 対象の出品情報の状態を[提出]に更新
 */
export const useBiddingItemStatusUpdateApply = () => {
  const SUCCESS_MESSAGE = "出品情報のステータスを更新しました。";  // 成功時メッセージ
  const ERROR_MESSAGE = "出品情報のステータス更新に失敗しました。";  // エラー時メッセージ
  const QUERY_KEY = { queryKey: ['biddingitemsstatusupdate', 'all'] } // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error,                  // error (TError)
    { request: BiddingItemStatusUpdateRequest} // variables (TVariables)
  >;
  
  const queryClient = useQueryClient();
  const { biddingItemStatusUpdateApply } = useItemsApi();

  return useMutation<mutationConfig['ret'], mutationConfig['error'], mutationConfig['variables']> ({
    
    mutationFn: (variables) => biddingItemStatusUpdateApply(variables.request),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 対象の出品情報の状態を[確定]に更新
 */
export const useBiddingItemStatusUpdateConfirm = () => {
  const SUCCESS_MESSAGE = "出品情報のステータスを更新しました。";  // 成功時メッセージ
  const ERROR_MESSAGE = "出品情報のステータス更新に失敗しました。";  // エラー時メッセージ
  const QUERY_KEY = { queryKey: ['biddingitemsstatusupdate', 'all'] } // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error,                  // error (TError)
    { request: BiddingItemStatusUpdateRequest} // variables (TVariables)
  >;
  
  const queryClient = useQueryClient();
  const { biddingItemStatusUpdateConfirm } = useItemsApi();

  return useMutation<mutationConfig['ret'], mutationConfig['error'], mutationConfig['variables']> ({
    
    mutationFn: (variables) => biddingItemStatusUpdateConfirm(variables.request),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
}